import { APIContext } from "../../types";
import { makePath } from "../../../../api/url";
import * as APITypesV1 from "../../../../api/cur8/generated-types/v1/Api";
import { getJSON, postJSON } from "../../request";
import { applyParams } from "../../../params";
import { applyPagination } from "../../pagination";

type QueryParams = Partial<APITypesV1.BookingListByResourceUriParams>;
export type BookingApi = ReturnType<typeof createBookingAPIv1>;

export function createBookingAPIv1({
  buildURL,
  fetchJSON,
  fetchRaw,
  fetchPagedJSON,
}: APIContext) {
  function querySlots({
    resourceURI,
    ...rest
  }: { resourceURI: string } & QueryParams) {
    const path = makePath(["api", "v1", "booking", resourceURI, "slot"]);
    const url = buildURL(path);

    applyParams(url.searchParams, {
      ...rest,
    });
    applyPagination(url.searchParams, { ...rest });

    const request = getJSON(url);

    return fetchPagedJSON<APITypesV1.Slot>(request);
  }

  function getScheduleView({
    bookingResourceUri,
    ...rest
  }: { bookingResourceUri: string } & APITypesV1.BookingGetScheduleViewParams) {
    const path = makePath([
      "api",
      "v1",
      "booking",
      bookingResourceUri,
      "schedule-view",
    ]);
    const url = buildURL(path);

    applyParams(url.searchParams, {
      ...rest,
    });

    const request = getJSON(url);

    return fetchJSON<APITypesV1.ScheduleView>(request);
  }

  function getSlot({
    resourceURI,
    slotId,
  }: {
    resourceURI: string;
    slotId: string;
  }) {
    const path = makePath([
      "api",
      "v1",
      "booking",
      resourceURI,
      "slot",
      slotId,
    ]);
    const url = buildURL(path);
    const request = getJSON(url);

    return fetchJSON<APITypesV1.Slot>(request);
  }

  function updateSlot({
    eTag,
    resourceURI,
    slotId,
    patientId,
    questions,
    allowDoubleBooking,
    isReserved,
  }: {
    eTag: string | undefined;
    resourceURI: string;
    slotId: string;
  } & APITypesV1.UpdateSlotRequest) {
    const path = makePath([
      "api",
      "v1",
      "booking",
      resourceURI,
      "slot",
      slotId,
    ]);
    const url = buildURL(path);

    const headers: Record<string, string> = {
      accept: "application/json",
      "content-type": "application/json",
    };

    if (patientId) {
      if (eTag == null) {
        throw new Error("eTag should be provided when updating patient slot");
      }
      headers["If-Match"] = eTag;
    }

    const request = new Request(url.toString(), {
      headers,
      method: "POST",
      body: JSON.stringify({
        patientId,
        questions,
        allowDoubleBooking,
        isReserved,
      }),
    });

    return fetchJSON<APITypesV1.Slot>(request);
  }

  function getBookingTokensForPatient(
    props: APITypesV1.BookingQueryTokensParams & { patientId: string }
  ) {
    const { patientId, ...rest } = props;
    const path = makePath([
      "api",
      "v1",
      "patient",
      patientId,
      "booking",
      "token",
    ]);
    const url = buildURL(path);

    applyParams(url.searchParams, {
      ...rest,
    });
    applyPagination(url.searchParams, {
      continuationToken: rest.continuationToken,
      pageSize: rest.pageSize,
    });

    const request = getJSON(url);

    return fetchPagedJSON<APITypesV1.BookingToken>(request);
  }

  function checkIn({
    roomId,
    slotId,
    eTag,
    patientId,
  }: {
    roomId: string;
    slotId: string;
    eTag: string;
  } & APITypesV1.CheckinSlotRequest) {
    const path = makePath([
      "api",
      "v1",
      "booking",
      `cur8:room:${roomId}`,
      "slot",
      slotId,
      "checkin",
    ]);
    const url = buildURL(path);

    const request = postJSON(url, { patientId });
    request.headers.set("If-Match", eTag);

    return fetchJSON<APITypesV1.Slot>(request);
  }

  function createBookingToken(body: APITypesV1.CreateBookingTokenRequest) {
    const path = makePath(["api", "v1", "booking", "token"]);
    const url = buildURL(path);
    const request = postJSON(url, body);

    return fetchJSON<APITypesV1.BookingToken>(request);
  }

  function createSlot({
    bookingResourceUri,
    ...rest
  }: { bookingResourceUri: string } & APITypesV1.CreateSlotRequest) {
    const path = makePath(["api", "v1", "booking", bookingResourceUri, "slot"]);

    const url = buildURL(path);
    const request = postJSON(url, rest);

    return fetchJSON<APITypesV1.Slot>(request);
  }

  function deleteSlot({
    bookingResourceUri,
    slotId,
    eTag,
  }: {
    bookingResourceUri: string;
    slotId: string;
    eTag: string | undefined;
  }) {
    if (eTag == null) {
      throw new Error("eTag should be provided when deleting a slot");
    }

    const path = makePath([
      "api",
      "v1",
      "booking",
      bookingResourceUri,
      "slot",
      slotId,
    ]);

    const url = buildURL(path);
    const request = new Request(url.toString(), {
      method: "DELETE",
      headers: {
        "If-Match": eTag,
      },
    });

    return fetchRaw(request);
  }

  function createAndBookSlot({
    patientId,
    bookingResourceUri,
    ...rest
  }: {
    // TODO: this should be generated from the API
    startTime: string;
    endTime: string;
    patientId: string;
    bookingResourceUri: string;
  }) {
    const path = makePath([
      "api",
      "v1",
      "patient",
      patientId,
      "booking",
      bookingResourceUri,
      "slot",
    ]);
    const url = buildURL(path);
    const request = postJSON(url, rest);

    return fetchJSON<APITypesV1.Slot>(request);
  }

  function getUpcomingFreeSlotsForPatient({
    patientId,
    bookingResourceUri,
    ...rest
  }: APITypesV1.BookingGetUpcomingFreeSlotsForPatientParams) {
    const path = makePath([
      "api",
      "v1",
      "patient",
      patientId,
      "booking",
      bookingResourceUri,
      "slots",
    ]);
    const url = buildURL(path);

    applyParams(url.searchParams, {
      ...rest,
    });
    applyPagination(url.searchParams, { ...rest });

    const request = getJSON(url);

    return fetchPagedJSON<APITypesV1.Slot>(request);
  }

  function claimBookingCapability({
    patientId,
    tokenSegment,
  }: {
    patientId: string;
    tokenSegment: string;
  }) {
    const path = makePath([
      "api",
      "v1",
      "patient",
      patientId,
      "booking",
      "token",
      tokenSegment,
      "claim",
    ]);
    const url = buildURL(path);
    const request = postJSON(url, {});

    return fetchRaw(request);
  }

  function getAvailableSlotCount({
    patientId,
    bookingResourceUri,
    ...rest
  }: APITypesV1.BookingGetUpcomingFreeSlotsCountForPatientParams) {
    const path = makePath([
      "api",
      "v1",
      "patient",
      patientId,
      "booking",
      bookingResourceUri,
      "slots",
      "count",
    ]);
    const url = buildURL(path);
    applyParams(url.searchParams, {
      ...rest,
    });

    const request = getJSON(url);

    return fetchJSON<number>(request);
  }

  return {
    checkIn,
    querySlots,
    getScheduleView,
    getSlot,
    createSlot,
    updateSlot,
    deleteSlot,
    getBookingTokensForPatient,
    createBookingToken,
    createAndBookSlot,
    getUpcomingFreeSlotsForPatient,
    claimBookingCapability,
    getAvailableSlotCount,
  };
}
